import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, PackageSearch, Search, Send, Truck } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import type { OrderRecord } from '@forq/orders-data';
import {
  createIdempotencyKey,
  needsPartsReview,
  partsAvailabilityLabels,
  supplierProviderLabels,
  supplierPurchaseOrderStatusLabels,
  workOrderAllowsPartsOrder,
  type PartsGateway,
  type PartsStockShortfall,
  type SupplierConnectionRecord,
  type SupplierProvider,
  type SupplierPurchaseOrderRecord,
  type SupplierPurchaseOrderStatus,
} from '@forq/parts-data';
import { StatusPill, type StatusTone } from '@forq/ui';

/**
 * Ersatzteile am Vorgang -- der EINZIGE Einstieg in eine Lieferantenbestellung.
 *
 * Es gibt bewusst keinen allgemeinen Bestellknopf irgendwo sonst in FORQ. Wer ein Teil braucht,
 * öffnet den Auftrag, für den er es braucht. Die Auftragsnummer und der Kunde stehen deshalb
 * dauerhaft im Kopf dieses Feldes: man soll nie im Zweifel sein, wofür man gerade bestellt.
 *
 * Der Ablauf, den die Oberfläche führt:
 *
 *   Bestand prüfen -> Fehlmenge sehen -> beim Lieferanten suchen -> Warenkorb kommt zurück
 *   -> Entwurf prüfen -> Preis/Verfügbarkeit erneut prüfen -> ggf. Abweichung bestätigen
 *   -> absenden -> Wareneingang buchen.
 */

interface OrderPartsPanelProps {
  readonly tenant: TenantSummary;
  readonly order: OrderRecord;
  readonly permissions: ReadonlySet<string>;
  readonly gateway: PartsGateway;
  /** Läuft der Bestelldienst des FORQ-Servers? Ohne ihn geht alles außer Suchen und Absenden. */
  readonly orderingAvailable: boolean;
  /** Nummer der Station in der Belegstrecke -- rendert das Nummern-Badge. */
  readonly stepNumber?: number;
}

const statusTone: Readonly<Record<SupplierPurchaseOrderStatus, StatusTone>> = {
  draft: 'neutral',
  validating: 'info',
  validated: 'info',
  submitting: 'progress',
  ordered: 'progress',
  confirmed: 'progress',
  'partially-delivered': 'warning',
  delivered: 'done',
  failed: 'danger',
  'requires-review': 'warning',
  'unknown-external-state': 'danger',
  cancelled: 'neutral',
};

function money(value: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(value);
}

function amount(value: number): string {
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 }).format(value);
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message
    ? error.message
    : 'Die Ersatzteilbestellung konnte nicht ausgeführt werden.';
}

export function OrderPartsPanel({
  tenant,
  order,
  permissions,
  gateway,
  orderingAvailable,
  stepNumber,
}: OrderPartsPanelProps) {
  const navigate = useNavigate();
  const [connections, setConnections] = useState<readonly SupplierConnectionRecord[]>([]);
  const [shortfalls, setShortfalls] = useState<readonly PartsStockShortfall[]>([]);
  const [orders, setOrders] = useState<readonly SupplierPurchaseOrderRecord[]>([]);
  const [provider, setProvider] = useState<SupplierProvider>('mock');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const mayOrder = permissions.has('parts.select');
  const maySubmit = permissions.has('parts.submit');
  const mayReceive = permissions.has('parts.goods.receive');
  const mayViewConnections = permissions.has('parts.admin');
  const statusAllows = workOrderAllowsPartsOrder(order.status);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [gefunden, fehlmengen] = await Promise.all([
        gateway.listForWorkOrder(tenant.id, order.id),
        mayOrder && statusAllows
          ? gateway.checkWorkOrderStock(tenant.id, order.id)
          : Promise.resolve([] as readonly PartsStockShortfall[]),
      ]);
      setOrders(gefunden);
      setShortfalls(fehlmengen);
    } catch (loadError) {
      setError(messageOf(loadError));
    }
  }, [gateway, tenant.id, order.id, mayOrder, statusAllows]);

  useEffect(() => {
    void load();
  }, [load]);

  // Die Anbindungen kennt nur, wer sie verwalten darf. Für alle anderen zeigt die Auswahl
  // schlicht die drei Lieferanten -- welche wirklich freigeschaltet sind, entscheidet der Server.
  useEffect(() => {
    if (!mayViewConnections) return;
    let active = true;
    gateway
      .listConnections(tenant.id)
      .then((geladen) => {
        if (!active) return;
        setConnections(geladen);
        const erste = geladen.find((entry) => entry.active && entry.punchoutEnabled);
        if (erste) setProvider(erste.provider);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [gateway, tenant.id, mayViewConnections]);

  const openDraft = useMemo(
    () => orders.find((entry) => ['draft', 'validating', 'validated'].includes(entry.status)) ?? null,
    [orders],
  );
  const reviewCases = useMemo(() => orders.filter((entry) => needsPartsReview(entry.status)), [orders]);
  const missingTotal = shortfalls.reduce((sum, entry) => sum + entry.missingQuantity, 0);

  // --- Aktionen ------------------------------------------------------------

  const search = useCallback(async () => {
    setError(null);
    setNote(null);
    setBusy('suchen');
    try {
      const sitzung = await gateway.startPunchoutSession({
        tenantId: tenant.id,
        workOrderId: order.id,
        provider,
        shortfallPositionIds: shortfalls.map((entry) => entry.positionId),
      });
      if (!sitzung.startUrl) {
        setNote(
          `Für ${supplierProviderLabels[provider]} liegt noch kein freigegebenes Suchverfahren vor. Die Sitzung wurde protokolliert, geöffnet wurde nichts.`,
        );
        return;
      }

      // Eine RELATIVE Adresse ist der Übungsshop -- der läuft in FORQ selbst und wird deshalb
      // im selben Fenster geöffnet. Ein neues Fenster hätte im Demo-Betrieb kein Gedächtnis
      // der gerade eröffneten Sitzung, und der Ablauf risse genau in der Mitte.
      // Eine absolute Adresse ist ein echter Lieferant -- der gehört in ein eigenes Fenster,
      // damit der Vorgang in FORQ offen bleibt.
      if (sitzung.startUrl.startsWith('/')) {
        navigate(sitzung.startUrl);
        return;
      }
      window.open(sitzung.startUrl, '_blank', 'noopener');
      setNote(
        `Die Suche bei ${supplierProviderLabels[provider]} ist in einem neuen Fenster geöffnet. Der Warenkorb kommt als Entwurf hierher zurück.`,
      );
    } catch (searchError) {
      setError(messageOf(searchError));
    } finally {
      setBusy(null);
    }
  }, [gateway, tenant.id, order.id, provider, shortfalls, navigate]);

  const validate = useCallback(
    async (purchaseOrderId: string) => {
      setError(null);
      setNote(null);
      setBusy(purchaseOrderId);
      try {
        const ergebnis = await gateway.validateOrder({ tenantId: tenant.id, purchaseOrderId });
        setNote(ergebnis.checkMessage);
        await load();
      } catch (validateError) {
        setError(messageOf(validateError));
      } finally {
        setBusy(null);
      }
    },
    [gateway, tenant.id, load],
  );

  const confirm = useCallback(
    async (purchaseOrderId: string) => {
      setError(null);
      setBusy(purchaseOrderId);
      try {
        await gateway.confirmDeviations({ tenantId: tenant.id, purchaseOrderId });
        setNote('Die Abweichungen sind bestätigt. Die Bestellung kann abgesendet werden.');
        await load();
      } catch (confirmError) {
        setError(messageOf(confirmError));
      } finally {
        setBusy(null);
      }
    },
    [gateway, tenant.id, load],
  );

  const submit = useCallback(
    async (purchaseOrderId: string) => {
      // Doppelklickschutz in der Oberfläche -- die eigentliche Sperre liegt in der Datenbank
      // (eindeutiger Idempotenzschlüssel), aber der Knopf soll gar nicht erst zweimal gehen.
      if (busy) return;
      setError(null);
      setNote(null);
      setBusy(purchaseOrderId);
      try {
        const bestellt = await gateway.submitOrder({
          tenantId: tenant.id,
          purchaseOrderId,
          idempotencyKey: createIdempotencyKey(),
        });
        setNote(
          bestellt.status === 'ordered'
            ? `Bestellt. Der Lieferant führt sie unter ${bestellt.externalOrderNumber}.`
            : `Stand: ${supplierPurchaseOrderStatusLabels[bestellt.status]}.`,
        );
        await load();
      } catch (submitError) {
        setError(messageOf(submitError));
      } finally {
        setBusy(null);
      }
    },
    [gateway, tenant.id, load, busy],
  );

  const refresh = useCallback(
    async (purchaseOrderId: string) => {
      setError(null);
      setBusy(purchaseOrderId);
      try {
        const aktuell = await gateway.refreshStatus({ tenantId: tenant.id, purchaseOrderId });
        setNote(`Stand beim Lieferanten: ${supplierPurchaseOrderStatusLabels[aktuell.status]}.`);
        await load();
      } catch (refreshError) {
        setError(messageOf(refreshError));
      } finally {
        setBusy(null);
      }
    },
    [gateway, tenant.id, load],
  );

  const receiveAll = useCallback(
    async (bestellung: SupplierPurchaseOrderRecord) => {
      setError(null);
      setBusy(bestellung.id);
      try {
        await gateway.receiveGoods({
          tenantId: tenant.id,
          purchaseOrderId: bestellung.id,
          lines: bestellung.lines
            .filter((line) => line.receivedQuantity < line.quantity)
            .map((line) => ({
              lineId: line.id,
              receivedQuantity: line.quantity - line.receivedQuantity,
            })),
        });
        setNote('Wareneingang gebucht.');
        await load();
      } catch (receiveError) {
        setError(messageOf(receiveError));
      } finally {
        setBusy(null);
      }
    },
    [gateway, tenant.id, load],
  );

  // --- Darstellung ---------------------------------------------------------

  return (
    <section className="order-parts" aria-label="Ersatzteile">
      <header className="order-parts__head">
        {stepNumber ? <span className="order-parts__num">{stepNumber}</span> : null}
        <div>
          <h3>
            <PackageSearch size={15} /> Ersatzteile
          </h3>
          {/* Auftragsnummer und Kunde bleiben sichtbar -- wofür bestellt wird, ist nie eine Vermutung. */}
          <p className="order-parts__context">
            Vorgang <strong>{order.number}</strong> · {order.customerName || 'ohne Kundenbezug'}
          </p>
        </div>
      </header>

      {error ? (
        <div className="order-parts__error" role="alert">
          {error}
        </div>
      ) : null}
      {note && !error ? <p className="order-parts__note">{note}</p> : null}

      {!statusAllows ? (
        <p className="order-parts__hint">
          Für einen Vorgang im Stand „{order.status}" können keine Ersatzteile bestellt werden.
        </p>
      ) : null}

      {!orderingAvailable ? (
        <p className="order-parts__hint">
          Suche und Absenden laufen über den FORQ-Server. In dieser Fassung sind Bestandsprüfung,
          Entwürfe und die Bestellhistorie verfügbar.
        </p>
      ) : null}

      {/* Bestandsabgleich -- der Auslöser */}
      {statusAllows && shortfalls.length > 0 ? (
        <div className="order-parts__shortfall">
          <p>
            <AlertTriangle size={14} /> {shortfalls.length} Position
            {shortfalls.length === 1 ? '' : 'en'} nicht vollständig auf Lager ({amount(missingTotal)}{' '}
            fehlend).
          </p>
          <ul>
            {shortfalls.map((entry) => (
              <li key={entry.positionId}>
                <span className="order-parts__shortfall-name">
                  Pos. {entry.positionNumber} · {entry.itemNumber} {entry.description}
                </span>
                <span className="order-parts__shortfall-amount">
                  {amount(entry.missingQuantity)} von {amount(entry.requiredQuantity)} {entry.unit}{' '}
                  fehlen
                </span>
                <span className="order-parts__shortfall-supplier">
                  {entry.preferredSupplierName || 'kein bevorzugter Lieferant'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Suche starten */}
      {statusAllows && mayOrder ? (
        <div className="order-parts__search">
          <label>
            Lieferant
            <select
              value={provider}
              onChange={(event) => setProvider(event.target.value as SupplierProvider)}
            >
              {(['mock', 'tvh', 'trex'] as const).map((entry) => {
                const verbindung = connections.find((item) => item.provider === entry);
                const gesperrt = mayViewConnections && verbindung
                  ? !verbindung.active || !verbindung.punchoutEnabled
                  : false;
                return (
                  <option key={entry} value={entry} disabled={gesperrt}>
                    {supplierProviderLabels[entry]}
                    {gesperrt ? ' (nicht freigeschaltet)' : ''}
                  </option>
                );
              })}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void search()}
            disabled={busy !== null || !orderingAvailable}
          >
            <Search size={14} /> {busy === 'suchen' ? 'Wird geöffnet …' : 'Ersatzteil suchen'}
          </button>
        </div>
      ) : null}

      {/* Der offene Entwurf -- alles, was vor dem Absenden zu sehen sein muss */}
      {openDraft ? (
        <PartsDraft
          draft={openDraft}
          busy={busy === openDraft.id}
          maySubmit={maySubmit}
          orderingAvailable={orderingAvailable}
          onValidate={() => void validate(openDraft.id)}
          onConfirm={() => void confirm(openDraft.id)}
          onSubmit={() => void submit(openDraft.id)}
        />
      ) : null}

      {/* Klärfälle: eigener, deutlich abgesetzter Bereich */}
      {reviewCases.length > 0 ? (
        <div className="order-parts__review">
          <h4>
            <AlertTriangle size={14} /> Zu klären ({reviewCases.length})
          </h4>
          {reviewCases.map((entry) => (
            <div className="order-parts__review-row" key={entry.id}>
              <span>
                <strong>{entry.number}</strong> · {supplierProviderLabels[entry.provider]}
              </span>
              <StatusPill label={supplierPurchaseOrderStatusLabels[entry.status]} tone={statusTone[entry.status]} />
              <span className="order-parts__review-reason">{entry.reviewReason || entry.failureReason}</span>
              <button
                type="button"
                className="is-ghost"
                onClick={() => void refresh(entry.id)}
                disabled={busy !== null || !orderingAvailable}
              >
                Beim Lieferanten nachfragen
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {/* Historie -- was aus diesem Vorgang schon bestellt wurde */}
      {orders.length > 0 ? (
        <details className="order-parts__history">
          <summary>Bestellungen zu diesem Vorgang ({orders.length})</summary>
          <div className="order-parts__history-table">
            <div className="order-parts__history-head">
              <span>Bestellung</span>
              <span>Lieferant</span>
              <span>Stand</span>
              <span>Beim Lieferanten</span>
              <span>Summe</span>
              <span>Aktion</span>
            </div>
            {orders.map((entry) => (
              <div className="order-parts__history-row" key={entry.id}>
                <span>
                  <strong>{entry.number}</strong>
                  <em>{new Date(entry.createdAt).toLocaleDateString('de-DE')}</em>
                </span>
                <span>{supplierProviderLabels[entry.provider]}</span>
                <span>
                  <StatusPill
                    label={supplierPurchaseOrderStatusLabels[entry.status]}
                    tone={statusTone[entry.status]}
                  />
                </span>
                <span>{entry.externalOrderNumber || '—'}</span>
                <span>{money(entry.totalAmount, entry.currency)}</span>
                <span>
                  {mayReceive && ['ordered', 'confirmed', 'partially-delivered'].includes(entry.status) ? (
                    <button
                      type="button"
                      className="is-ghost"
                      onClick={() => void receiveAll(entry)}
                      disabled={busy !== null}
                    >
                      <Truck size={13} /> Wareneingang
                    </button>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        </details>
      ) : null}

      {orders.length === 0 && shortfalls.length === 0 ? (
        <p className="order-parts__empty">
          Zu diesem Vorgang wurde noch nichts bestellt. Fehlende Teile erscheinen hier, sobald sie als
          Position eingetragen sind.
        </p>
      ) : null}
    </section>
  );
}

/** Der Entwurf, wie ihn der Besteller vor dem Absenden sieht -- vollständig, ohne Klickstrecke. */
function PartsDraft({
  draft,
  busy,
  maySubmit,
  orderingAvailable,
  onValidate,
  onConfirm,
  onSubmit,
}: {
  readonly draft: SupplierPurchaseOrderRecord;
  readonly busy: boolean;
  readonly maySubmit: boolean;
  readonly orderingAvailable: boolean;
  readonly onValidate: () => void;
  readonly onConfirm: () => void;
  readonly onSubmit: () => void;
}) {
  const materialDeviations = draft.deviations.filter((entry) => entry.material);
  const wartetAufBestaetigung = draft.status === 'draft' && materialDeviations.length > 0;
  const bereit = draft.status === 'validated';

  return (
    <div className="order-parts__draft">
      <div className="order-parts__draft-head">
        <span>
          <strong>{draft.number}</strong> · {supplierProviderLabels[draft.provider]}
        </span>
        <StatusPill
          label={supplierPurchaseOrderStatusLabels[draft.status]}
          tone={statusTone[draft.status]}
        />
      </div>

      <div className="order-parts__lines">
        <div className="order-parts__lines-head">
          <span>Artikelnummer</span>
          <span>Hersteller</span>
          <span>Bezeichnung</span>
          <span>Menge</span>
          <span>Einzelpreis</span>
          <span>Verfügbarkeit</span>
          <span>Lieferzeit</span>
          <span>Summe</span>
        </div>
        {draft.lines.map((line) => (
          <div className="order-parts__lines-row" key={line.id}>
            <span>{line.supplierItemNumber}</span>
            <span>{line.manufacturer || '—'}</span>
            <span>{line.description}</span>
            <span>
              {amount(line.quantity)} {line.unit}
            </span>
            <span>{money(line.unitPrice, line.currency)}</span>
            <span>{partsAvailabilityLabels[line.availability]}</span>
            <span>{line.leadTimeDays == null ? 'unbekannt' : `${line.leadTimeDays} Tage`}</span>
            <span>{money(line.lineTotal, line.currency)}</span>
          </div>
        ))}
      </div>

      <dl className="order-parts__summary">
        <div>
          <dt>Lieferadresse</dt>
          <dd>
            {draft.deliveryAddress.companyName || draft.deliveryAddress.label},{' '}
            {draft.deliveryAddress.street} {draft.deliveryAddress.postalCode}{' '}
            {draft.deliveryAddress.city}
          </dd>
        </div>
        <div>
          <dt>Versandart</dt>
          <dd>{draft.shippingMethod || 'nach Vorgabe des Lieferanten'}</dd>
        </div>
        <div>
          <dt>Nettosumme</dt>
          <dd>{money(draft.netTotal, draft.currency)}</dd>
        </div>
        <div>
          <dt>Versandkosten</dt>
          <dd>{money(draft.shippingCost, draft.currency)}</dd>
        </div>
        <div className="is-total">
          <dt>Gesamtsumme</dt>
          <dd>{money(draft.totalAmount, draft.currency)}</dd>
        </div>
      </dl>

      {draft.deviations.length > 0 ? (
        <div className={`order-parts__deviations${wartetAufBestaetigung ? ' is-blocking' : ''}`}>
          <h5>
            <AlertTriangle size={14} /> Seit der Übernahme geändert
          </h5>
          <ul>
            {draft.deviations.map((entry, index) => (
              <li key={`${entry.kind}-${entry.lineId ?? 'gesamt'}-${index}`}>
                <span className="order-parts__deviation-label">
                  {entry.label}
                  {entry.material ? ' · wesentlich' : ''}
                </span>
                <span className="order-parts__deviation-old">{entry.previousValue}</span>
                <span className="order-parts__deviation-arrow">→</span>
                <span className="order-parts__deviation-new">{entry.currentValue}</span>
              </li>
            ))}
          </ul>
          {wartetAufBestaetigung ? (
            <p>
              Wesentliche Änderung. Ohne erneute Bestätigung wird nicht abgesendet.
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="order-parts__draft-actions">
        {draft.status === 'draft' && !wartetAufBestaetigung ? (
          <button type="button" onClick={onValidate} disabled={busy || !orderingAvailable}>
            {busy ? 'Wird geprüft …' : 'Preis und Verfügbarkeit prüfen'}
          </button>
        ) : null}
        {wartetAufBestaetigung ? (
          <button type="button" onClick={onConfirm} disabled={busy || !maySubmit}>
            {busy ? 'Wird bestätigt …' : 'Geänderte Werte bestätigen'}
          </button>
        ) : null}
        {bereit ? (
          <button
            type="button"
            className="is-primary"
            onClick={onSubmit}
            disabled={busy || !maySubmit || !orderingAvailable}
          >
            <Send size={14} /> {busy ? 'Wird gesendet …' : 'Verbindlich bestellen'}
          </button>
        ) : null}
        {!maySubmit ? (
          <span className="order-parts__hint">
            Zum Absenden fehlt die Berechtigung. Der Entwurf kann von der Betriebs- oder
            Serviceleitung abgeschickt werden.
          </span>
        ) : null}
      </div>
    </div>
  );
}
